import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import TicketTableArchive from './TicketTableArchive';
import { PageHeader } from '../design/ui';
import { TL_NS, fmtFr } from './TicketListsShared';
import './Archive.css';

const PAGE_SIZE = 25;

const Archive = () => {
  const { t } = useTranslation();
  const tl = (key, fallback, options) => t(`${TL_NS}.${key}`, fallback, options);
  const [archivedTickets, setArchivedTickets] = useState([]);
  const [user, setUser] = useState(undefined);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  // nonRequests: the e-mails the AI closed as « not a request » (KI-282), listed alone.
  const [archiveFilters, setArchiveFilters] = useState({ status: '', resolvedBy: '', nonRequests: false });
  // Total of the other kind (tickets ↔ non-requests) under the same filters,
  // so the kind filter tells what it leads to before the click.
  const [otherKindTotal, setOtherKindTotal] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      navigate('/');
      return;
    }
    fetchUserData(token);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  useEffect(() => {
    if (user === null) {
      navigate('/');
    }
  }, [user, navigate]);

  useEffect(() => {
    if (user) {
      const token = localStorage.getItem('access_token');
      fetchArchivedTickets(token, currentPage, archiveFilters);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, currentPage, archiveFilters]);

  // The other kind's count: one ticket asked, only the total read.
  useEffect(() => {
    if (!user) return undefined;
    let alive = true;
    const token = localStorage.getItem('access_token');
    axios.get('/api/tickets/archived', {
      headers: { Authorization: `Bearer ${token}` },
      params: {
        page: 1,
        limit: 1,
        status: archiveFilters.status || undefined,
        resolved_by: archiveFilters.resolvedBy || undefined,
        non_requests: archiveFilters.nonRequests ? undefined : true,
      }
    })
      .then((response) => {
        const value = response.data?.pagination?.total_tickets;
        if (alive) setOtherKindTotal(Number.isFinite(value) ? value : null);
      })
      .catch(() => { if (alive) setOtherKindTotal(null); });
    return () => { alive = false; };
  }, [user, archiveFilters]);

  const fetchUserData = async (token) => {
    try {
      const response = await axios.get('/api/users/user', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error(t('archiveP.fetchUserDataError'), error);
      setUser(null);
    }
  };

  const fetchArchivedTickets = async (token, page, filters) => {
    setIsLoading(true);
    try {
      const response = await axios.get('/api/tickets/archived', {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          page,
          limit: PAGE_SIZE,
          status: filters.status || undefined,
          resolved_by: filters.resolvedBy || undefined,
          non_requests: filters.nonRequests ? true : undefined,
        }
      });
      setArchivedTickets(response.data.tickets);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error(t('archiveP.fetchArchivedTicketsError'), error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTicketClick = (ticketId) => {
    navigate(`/tickets/${ticketId}/messages`);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleFilterChange = (filters) => {
    setCurrentPage(1);
    setArchiveFilters(filters);
  };

  const currentTotal = !isLoading && Number.isFinite(pagination?.total_tickets) ? pagination.total_tickets : null;
  const kindCounts = archiveFilters.nonRequests
    ? { tickets: otherKindTotal, nonRequests: currentTotal }
    : { tickets: currentTotal, nonRequests: otherKindTotal };
  const filtered = Boolean(archiveFilters.status || archiveFilters.resolvedBy);

  let subtitle = tl('archive.subtitle', 'Les tickets résolus ou clos, et les e-mails que l’IA a écartés.');
  if (kindCounts.tickets !== null && kindCounts.tickets !== undefined) {
    const parts = [tl('archive.closedCount', '{{value}} tickets clos', { count: kindCounts.tickets, value: fmtFr(kindCounts.tickets) })];
    if (Number.isFinite(kindCounts.nonRequests)) {
      parts.push(tl('archive.nonRequestsCount', '{{value}} non-demandes', { count: kindCounts.nonRequests, value: fmtFr(kindCounts.nonRequests) }));
    }
    subtitle = `${parts.join(' · ')}${filtered ? ` ${tl('archive.withFilters', '(avec les filtres choisis)')}` : ''}`;
  }

  return (
    <div className="u-page archive-v2" id="unanimity-archive-dashboard">
      <PageHeader title={tl('archive.title', 'Archives')} subtitle={subtitle} />
      <div className="u-well">
        <TicketTableArchive
          tickets={archivedTickets}
          user={user}
          onTicketClick={handleTicketClick}
          isLoading={isLoading || user === undefined}
          pagination={pagination}
          onPageChange={handlePageChange}
          statusFilter={archiveFilters.status}
          resolvedByFilter={archiveFilters.resolvedBy}
          nonRequestsFilter={archiveFilters.nonRequests}
          onFilterChange={handleFilterChange}
          kindCounts={kindCounts}
        />
      </div>
    </div>
  );
};

export default Archive;
